import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Bell, CheckCheck, Trash2, Inbox } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_BADGE = {
  material_request_submitted: { label: 'Material Approval', cls: 'bg-orange-100 text-orange-800' },
  material_request_approved: { label: 'Approved', cls: 'bg-green-100 text-green-800' },
  material_request_rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-800' },
  order_placed: { label: 'New Order', cls: 'bg-blue-100 text-blue-800' },
  order_status_changed: { label: 'Order Update', cls: 'bg-blue-100 text-blue-800' },
  invoice_created: { label: 'Invoice', cls: 'bg-purple-100 text-purple-800' },
  delivery_note_created: { label: 'Delivery', cls: 'bg-cyan-100 text-cyan-800' },
  carton_scanned: { label: 'Carton Scan', cls: 'bg-emerald-100 text-emerald-800' },
  qr_batch_generated: { label: 'QR Batch', cls: 'bg-indigo-100 text-indigo-800' },
  low_stock: { label: 'Low Stock', cls: 'bg-rose-100 text-rose-800' },
};

const formatFull = (iso) => (iso ? new Date(iso).toLocaleString() : '');

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications?limit=100`, { headers: authHeaders() });
      setItems(res.data || []);
    } catch (_) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, { headers: authHeaders() });
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (_) {
      toast.error('Failed to mark as read');
    }
  };

  const markAll = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { headers: authHeaders() });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (_) {
      toast.error('Failed to mark all read');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`, { headers: authHeaders() });
      setItems((prev) => prev.filter((n) => n.id !== id));
    } catch (_) {
      toast.error('Failed to delete');
    }
  };

  const filtered = items.filter((n) => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.read;
    return n.type === filter;
  });

  const unreadCount = items.filter((n) => !n.read).length;

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Bell className="text-[#EA5829]" size={28} />
            Notifications
          </h1>
          <p className="text-slate-600 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'} · {items.length} total
          </p>
        </div>
        {unreadCount > 0 && (
          <Button onClick={markAll} className="bg-[#8FC043] hover:bg-[#7aa936]" data-testid="page-mark-all-read-btn">
            <CheckCheck size={16} className="mr-2" />
            Mark all as read
          </Button>
        )}
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="all">All ({items.length})</TabsTrigger>
          <TabsTrigger value="unread">Unread ({unreadCount})</TabsTrigger>
          <TabsTrigger value="material_request_submitted">Approvals</TabsTrigger>
          <TabsTrigger value="order_placed">Orders</TabsTrigger>
          <TabsTrigger value="invoice_created">Invoices</TabsTrigger>
          <TabsTrigger value="delivery_note_created">Deliveries</TabsTrigger>
          <TabsTrigger value="carton_scanned">Scans</TabsTrigger>
          <TabsTrigger value="low_stock">Low Stock</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 text-center text-slate-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-500">
              <Inbox size={40} className="mx-auto mb-3 text-slate-300" />
              <p className="font-semibold">No notifications</p>
              <p className="text-sm">When something needs your attention, it will appear here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((n) => {
                const badge = TYPE_BADGE[n.type] || { label: n.type, cls: 'bg-slate-100 text-slate-700' };
                return (
                  <li
                    key={n.id}
                    className={`flex items-start gap-3 p-4 hover:bg-slate-50 transition-colors ${
                      n.read ? '' : 'bg-blue-50/40'
                    }`}
                    data-testid={`notification-row-${n.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>
                          {badge.label}
                        </span>
                        {!n.read && (
                          <span className="text-[10px] font-bold uppercase text-[#EA5829]">New</span>
                        )}
                        <span className="ml-auto text-xs text-slate-500">{formatFull(n.created_at)}</span>
                      </div>
                      <button
                        onClick={async () => {
                          if (!n.read) await markRead(n.id);
                          if (n.link) navigate(n.link);
                        }}
                        className="text-left w-full"
                      >
                        <div className="text-sm font-semibold text-slate-900">{n.title}</div>
                        <div className="text-sm text-slate-600 mt-1">{n.message}</div>
                      </button>
                    </div>
                    <div className="flex flex-col gap-1">
                      {!n.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markRead(n.id)}
                          title="Mark as read"
                          data-testid={`mark-read-btn-${n.id}`}
                        >
                          <CheckCheck size={14} />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(n.id)}
                        title="Delete"
                        data-testid={`delete-notification-btn-${n.id}`}
                      >
                        <Trash2 size={14} className="text-rose-500" />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
